//#region IMPORT
import React, { useCallback, useEffect, useRef, useState } from 'react';
import M_TBG_CUSTOM_07 from './M_TBG_CUSTOM_07';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { VoiceRecorder } from 'capacitor-voice-recorder';
import { Capacitor } from '@capacitor/core';
import { useDispatch, useSelector } from 'react-redux';
import { CommonActions } from '../../../../Reducer/CommonReducer';
import {
  useSearch1,
  useSave1,
  useSave2,
} from './M_TBG_CUSTOM_07_Query';
import { useSave } from '../../../../Hooks/useSave';
import useFileUpload from '../../../../Hooks/useFileUpload';
//#endregion

function M_TBG_CUSTOM_07F(props) {
  //#region 변수
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const dispatch = useDispatch();
  const activeStep = useSelector((state) => state.common.activeStep);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'info',
  });
  const [codeList, setCodeList] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState('00:00');
  const [recordedFile, setRecordedFile] = useState(null);
  const [gjList, setGjList] = useState([]);
  const [state, setState] = useState({
    bottom: false,
    type: '',
  });
  const [duration, setDuration] = useState(600);
  const [current, setCurrent] = useState(0);

  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const recordRef = useRef(null);

  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  const tbmUpload = useFileUpload({
    oldFilePathField: 'tbmAddMeeting',
    newFileNameField: 'saveName',
    getTargetPath: () => `TBM/${props.tbmCdRef.current}/Audio`,
  });
  //#endregion

  //#region 이벤트
  useEffect(() => {
    Init();

    return () => {
      tbmUpload.clearFiles();
    };
  }, []);

  const Init = async () => {
    try {
      await Search1Async({ tbmCd: props.tbmCdRef.current });
    } catch (e) {
      console.log(e);
      setSnackbar({
        ...snackbar,
        open: true,
        message: e.message,
        type: 'error',
      });
    }
  };

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const elapsedSeconds = Math.floor(
        (now.getTime() - props.startTimeRef.current?.getTime()) / 1000
      );
      setCurrent(elapsedSeconds);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);

    return () => clearInterval(interval);
  }, [props.startTimeRef.current]);

  const InitWaveSurfer = useCallback(() => {
    if (wavesurferRef.current) {
      wavesurferRef.current.destroy();
    }

    const wavesurfer = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: '#A8DBA8',
      progressColor: '#3B8686',
      cursorColor: '#3B8686',
      height: 100,
    });

    const record = wavesurfer.registerPlugin(
      RecordPlugin.create({
        renderRecordedAudio: true,
        scrollingWaveform: true,
        continuousWaveformDuration: 30,
      })
    );

    record.on('record-progress', (time) => {
      setProgress(FormatTime(time));
    });

    record.on('record-end', (blob) => {
      const url = URL.createObjectURL(blob);
      wavesurferRef.current.empty();
      wavesurferRef.current.load(url);

      const audioFile = new File(
        [blob],
        `${new Date()
          .toISOString()
          .replace(/T/, '')
          .replace(/\..+/, '')
          .replace(/-/g, '')
          .replace(/:/g, '')}.mp3`,
        { type: 'audio/mp3' }
      );

      setRecordedFile(audioFile);
      tbmUpload.FileChange({
        target: { files: [audioFile] },
      });

      wavesurferRef.current.on('finish', () => {
        setIsPlaying(false);
      });
    });

    recordRef.current = record;
    wavesurferRef.current = wavesurfer;
    setProgress('00:00');
  }, [
    wavesurferRef,
    waveformRef,
    recordRef,
    setProgress,
    setRecordedFile,
    setIsPlaying,
  ]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      InitWaveSurfer();
    } else {
      setProgress('00:00');
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
      }
      if (Capacitor.isNativePlatform()) {
        VoiceRecorder.removeAllListeners();
      }
    };
  }, [InitWaveSurfer, setProgress, wavesurferRef]);
  //#endregion

  //#region 쿼리
  const { runAsync: Search1Async } = useSearch1({
    onSuccess: (rows) => {
      setRowData(rows);
    },
  });
  const { runAsync: Save1Async } = useSave1();
  const { runAsync: Save2Async } = useSave2();

  // 저장
  const { run: Save1 } = useSave({
    menuId: 'M_TBG_CUSTOM_07',
    fileSave: async () => {
      const finalPath = await tbmUpload.SaveLogicFile();
      const durationSec = await GetAudioDurationFromFile(recordedFile);
      const durationSeconds = Math.floor(durationSec);

      return {
        tbmAddMeeting: finalPath ||
          `TBM/${props.tbmCdRef.current}/Audio/${recordedFile.name}`,
        tbmAddMeetingTime: durationSeconds
      };
    },
    requestSave: (rows) => Save1Async([{
      tbmCd: props.tbmCdRef.current,
      tbmAddMeeting:
        rows[0].tbmAddMeeting ||
        `TBM/${props.tbmCdRef.current}/Audio/${recordedFile.name}`,
      tbmAddMeetingTime: rows[0].durationSeconds || 0,
    }]),
    onSuccess: (rows) => {
      Save2Async([{ eStep: '2', mStep: '7', tbmCd: props.tbmCdRef.current }]);
      dispatch(CommonActions.setActiveStep(activeStep + 1));
      tbmUpload.clearFiles();
    },
  });
  //#endregion

  //#region 나머지 함수
  const GetAudioDurationFromFile = (file) => {
    return new Promise((resolve, reject) => {
      const audio = document.createElement('audio');
      audio.preload = 'metadata';

      audio.onloadedmetadata = () => {
        if (audio.duration === Infinity) {
          audio.currentTime = 1e101;
          audio.ontimeupdate = () => {
            audio.ontimeupdate = null;
            resolve(audio.duration);
            audio.currentTime = 0;
          };
        } else {
          resolve(audio.duration);
        }
      };

      audio.onerror = () => {
        reject(new Error('오디오 파일을 읽을 수 없습니다.'));
      };

      audio.src = URL.createObjectURL(file);
    });
  };

  const FormatTime = (ms) => {
    const min = Math.floor((ms % 3600000) / 60000);
    const sec = Math.floor((ms % 60000) / 1000);
    return `${min < 10 ? '0' + min : min}:${sec < 10 ? '0' + sec : sec}`;
  };

  const UpdateNativeRecordingTime = () => {
    if (recordingStartTimeRef.current) {
      const elapsed = Date.now() - recordingStartTimeRef.current;
      const formattedTime = FormatTime(elapsed);
      setProgress(formattedTime);
      animationFrameRef.current = requestAnimationFrame(
        UpdateNativeRecordingTime
      );
    }
  };

  const StartNativeRecording = async () => {
    try {
      const canRecord = await VoiceRecorder.canDeviceVoiceRecord();
      if (!canRecord.value) {
        throw new Error('Cannot record on this device');
      }

      const hasPerm = await VoiceRecorder.hasAudioRecordingPermission();
      if (!hasPerm.value) {
        const permRes = await VoiceRecorder.requestAudioRecordingPermission();
        if (!permRes.value) {
          throw new Error('Microphone permission denied');
        }
      }

      await VoiceRecorder.startRecording();

      setIsRecording(true);
      recordingStartTimeRef.current = Date.now();

      UpdateNativeRecordingTime();
    } catch (error) {
      console.error('[Capacitor] Native recording start error:', error);
      setIsRecording(false);
    }
  };

  const StartPWARecording = async () => {
    if (!recordRef.current) return;
    try {
      await recordRef.current.startRecording();
      setIsRecording(true);
    } catch (error) {
      console.error('PWA recording start error:', error);
      setIsRecording(false);
    }
  };

  const StartRecording = async () => {
    tbmUpload.clearFiles();
    if (Capacitor.isNativePlatform()) {
      await StartNativeRecording();
    } else {
      await StartPWARecording();
    }
  };

  const StopNativeRecording = async () => {
    try {
      setIsRecording(false);

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      const data = await VoiceRecorder.stopRecording();

      if (recordingStartTimeRef.current) {
        recordingStartTimeRef.current = null;
      }

      if (data.value && data.value.recordDataBase64) {
        const blob = Base64toBlob(data.value.recordDataBase64, 'audio/mp3');
        const file = new File(
          [blob],
          `${new Date()
            .toISOString()
            .replace(/T/, '')
            .replace(/\..+/, '')
            .replace(/-/g, '')
            .replace(/:/g, '')}.mp3`,
          { type: 'audio/mp3' }
        );

        AnalyzeAndDrawWaveform(blob);

        setRecordedFile(file);
        tbmUpload.FileChange({
          target: { files: [file] },
        });
      }
    } catch (error) {
      console.error('[Capacitor] Native recording stop error:', error);
      setIsRecording(false);

      if (recordingStartTimeRef.current) {
        recordingStartTimeRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }
  };

  const StopPWARecording = () => {
    if (!recordRef.current) return;
    recordRef.current.stopRecording();
    setIsRecording(false);
  };

  const StopRecording = async () => {
    if (Capacitor.isNativePlatform()) {
      await StopNativeRecording();
    } else {
      StopPWARecording();
    }
  };

  const PlayRecording = () => {
    if (Capacitor.isNativePlatform()) {
      if (!recordedFile) return;

      const audio = new Audio(URL.createObjectURL(recordedFile));
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        audio.play();
        setIsPlaying(true);
        audio.onended = () => setIsPlaying(false);
      }
    } else {
      if (!wavesurferRef.current) return;
      wavesurferRef.current.playPause();
      setIsPlaying(wavesurferRef.current.isPlaying());
    }
  };

  const AnalyzeAndDrawWaveform = async (blob) => {
    try {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      const arrayBuffer = await blob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      const channelData = audioBuffer.getChannelData(0);
      const amp = canvas.height / 2;

      ctx.beginPath();
      ctx.strokeStyle = '#3B8686';
      ctx.lineWidth = 1.5;

      const step = Math.ceil(channelData.length / canvas.width);

      for (let i = 0; i < canvas.width; i++) {
        let min = 1.0;
        let max = -1.0;

        for (let j = 0; j < step; j++) {
          const idx = i * step + j;
          if (idx < channelData.length) {
            const datum = channelData[idx];
            if (datum < min) min = datum;
            if (datum > max) max = datum;
          }
        }

        ctx.moveTo(i, (1 + min) * amp);
        ctx.lineTo(i, (1 + max) * amp);
      }

      ctx.stroke();

      ctx.beginPath();
      ctx.strokeStyle = '#A8DBA8';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.moveTo(0, amp);
      ctx.lineTo(canvas.width, amp);
      ctx.stroke();
      ctx.setLineDash([]);
    } catch (error) {
      console.error('[Capacitor] Error analyzing waveform:', error);
    }
  };

  const Base64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    return new Blob(byteArrays, { type: contentType });
  };
  //#endregion

  return (
    <M_TBG_CUSTOM_07
      userInfo={userInfo}
      codeList={codeList}
      setCodeList={setCodeList}
      rowData={rowData}
      setRowData={setRowData}
      isRecording={isRecording}
      setIsRecording={setIsRecording}
      isPlaying={isPlaying}
      setIsPlaying={setIsPlaying}
      progress={progress}
      setProgress={setProgress}
      recordedFile={recordedFile}
      gjList={gjList}
      setGjList={setGjList}
      state={state}
      setState={setState}
      duration={duration}
      setDuration={setDuration}
      current={current}
      setCurrent={setCurrent}
      waveformRef={waveformRef}
      wavesurferRef={wavesurferRef}
      recordRef={recordRef}
      canvasRef={canvasRef}
      SaveLogic1={Save1}
      StartRecording={StartRecording}
      StopRecording={StopRecording}
      PlayRecording={PlayRecording}
    />
  );
}

export default M_TBG_CUSTOM_07F;
